<script setup lang="ts">
    import { computed, useTemplateRef, defineAsyncComponent } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useDropZone } from '@vueuse/core'
    import { storeToRefs } from 'pinia'
    import PkAuroraCanvas from './PkAuroraCanvas.vue'
    import PkAvatar from './PkAvatar.vue'
    import PkChatbotMessages from './PkChatbotMessages.vue'
    import PkChatbotInput from './PkChatbotInput.vue'
    import PkChatbotEmptyState from './PkChatbotEmptyState.vue'
    import {
        useChatbotStore,
        useLocalizedString,
        useGreeting,
    } from 'composables'
    import type { UIChatMessage } from 'models'

    const PkToolShowForm = defineAsyncComponent(
        () => import('./PkToolShowForm.vue'),
    )
    const PkToolRequestConfirm = defineAsyncComponent(
        () => import('./PkToolRequestConfirm.vue'),
    )
    const PkToolShowContactForm = defineAsyncComponent(
        () => import('./PkToolShowContactForm.vue'),
    )
    const PkToolShowSuggestedReply = defineAsyncComponent(
        () => import('./PkToolShowSuggestedReply.vue'),
    )
    const PkToolShowSources = defineAsyncComponent(
        () => import('./PkToolShowSources.vue'),
    )
    const PkToolRequestGeolocation = defineAsyncComponent(
        () => import('./PkToolRequestGeolocation.vue'),
    )
    const PkToolRequestOAuthConnection = defineAsyncComponent(
        () => import('./PkToolRequestOAuthConnection.vue'),
    )
    const PkToolShowLocation = defineAsyncComponent(
        () => import('./PkToolShowLocation.vue'),
    )
    const PkToolShowDiagram = defineAsyncComponent(
        () => import('./PkToolShowDiagram.vue'),
    )

    const props = defineProps<{
        agentId: string
        /** Mirrors the PkChatbot `modifier` prop: `fullscreen` shows a centered empty state (greeting) instead of the welcome message */
        modifier?: 'widget' | 'fullscreen'
        /** Display name of the current user, used for the `{name}` greeting placeholder */
        userName?: string
    }>()

    const emit = defineEmits<{
        'show-info': [message: UIChatMessage]
        revise: [message: UIChatMessage]
    }>()

    const store = useChatbotStore(props.agentId)

    const {
        agentInterface,
        agentFileUpload,
        actions,
        revisedAnswers,
        messages,
        chat,
        messageFeedbacks,
        feedbackDialogMessage,
        isFeedbackSubmitting,
        isFeedbackSubmitted,
        feedbackSubmitError,
        isLeadSubmitted,
        isLoadingSubmitLead,
        submitLeadError,
        input,
        inputMessagePlaceholder,
        isConversationBlocked,
        baseUrl,
        pendingAttachments,
        isAttachmentLimitReached,
        fileLimitError,
        apiClient,
        isDark,
    } = storeToRefs(store)

    const {
        handleSubmit: storeHandleSubmit,
        stopGeneration,
        regenerate,
        onUpvote,
        onDownvote,
        onFeedback,
        onFeedbackSubmit,
        onLeadSubmit,
        startNewChat,
        addToolOutput,
        handleFileSelect,
        clearFileLimitError,
    } = store

    const { t: $t } = useI18n({ useScope: 'global' })

    const attachmentNotice = computed(() => {
        const err = fileLimitError.value
        if (!err) {
            return undefined
        }
        return err.reason === 'per-chat'
            ? $t('message.fileLimitPerChat', { max: err.max })
            : $t('message.fileLimitPerMessage', { max: err.max })
    })

    const dismissableNotice = useLocalizedString(
        () => agentInterface.value?.dismissableNotice,
    )

    // Fullscreen empty state: shown until the user sends the first message.
    // The synthetic welcome message is suppressed by the store in fullscreen
    // (`hideWelcomeMessage`), the greeting takes its place.
    const hasUserMessages = computed(() =>
        messages.value.some((message) => message.role === 'user'),
    )
    const showEmptyState = computed(
        () => props.modifier === 'fullscreen' && !hasUserMessages.value,
    )
    const greeting = useGreeting(agentInterface, () => props.userName)

    const chatViewEl = useTemplateRef<HTMLDivElement>('chatViewEl')

    const handleExpandSourceContext = async (payload: {
        documentId: string
        chunkIndex: number
    }) => {
        const result = await apiClient.value.expandSourceContext(
            props.agentId,
            payload.documentId,
            payload.chunkIndex,
        )
        return result.content
    }

    const handleDownloadSource = async (documentId: string) => {
        const result = await apiClient.value.downloadSourceDocument(
            props.agentId,
            documentId,
        )
        window.open(result.downloadUrl, '_blank')
    }

    const handleFileDrop = (files: File[] | null) => {
        if (!agentFileUpload.value?.enabled || !files) {
            return
        }
        for (const file of files) {
            handleFileSelect(file)
        }
    }

    const { isOverDropZone } = useDropZone(chatViewEl, {
        dataTypes: computed(
            () => agentFileUpload.value?.allowedMimeTypes ?? [],
        ),
        onDrop: handleFileDrop,
    })
</script>

<template>
    <div
        ref="chatViewEl"
        class="pk-chatbot-view-chat"
        :class="{
            'pk-chatbot-view-chat--dragover':
                isOverDropZone && agentFileUpload?.enabled,
            'pk-chatbot-view-chat--empty': showEmptyState,
        }">
        <!-- #region empty state (fullscreen) / messages — crossfade out-in -->
        <Transition name="pk-chatbot-view-chat-fade" mode="out-in">
            <PkChatbotEmptyState
                v-if="showEmptyState"
                :greeting="greeting"
                :logo="agentInterface?.logo"
                :name="store.name" />
            <PkChatbotMessages
                v-else
                class="flex flex-col flex-1 min-h-0 p-md overflow-y-auto"
                :messages="messages"
                :status="chat.status"
                :error="chat.error"
                :main-color="agentInterface?.mainColor"
                :text-color="agentInterface?.textColor"
                :revised-answers="revisedAnswers"
                :actions="actions"
                :message-feedbacks="messageFeedbacks"
                :feedback-message-id="feedbackDialogMessage?.id"
                :feedback-loading="isFeedbackSubmitting"
                :feedback-submitted="isFeedbackSubmitted"
                :feedback-error="feedbackSubmitError"
                :show-extended-steps="agentInterface?.showExtendedSteps"
                :is-dark="isDark"
                :show-scroll-to-bottom="modifier === 'fullscreen'"
                @feedback-submit="onFeedbackSubmit($event)"
                @feedback-close="feedbackDialogMessage = undefined"
                @regenerate="regenerate"
                @auto-retry="regenerate"
                @reset-chat="startNewChat"
                @show-info="emit('show-info', $event)"
                @revise="emit('revise', $event)"
                @upvote="onUpvote"
                @downvote="onDownvote"
                @feedback="onFeedback">
                <template #tool-showContactForm="{ part }">
                    <PkToolShowContactForm
                        :part
                        :readonly="!baseUrl"
                        :submitted="isLeadSubmitted"
                        :loading="isLoadingSubmitLead"
                        :error="submitLeadError"
                        :privacy-policy-notice="
                            agentInterface?.privacyPolicyNotice
                        "
                        @submit="onLeadSubmit" />
                </template>
                <template #tool-showSuggestedReply="{ part }">
                    <PkToolShowSuggestedReply
                        :part
                        @select="
                            ($event) => {
                                input = $event
                                storeHandleSubmit()
                            }
                        " />
                </template>
                <template #tool-showSources="{ part }">
                    <PkToolShowSources
                        :part
                        :on-expand-context="handleExpandSourceContext"
                        :on-download="handleDownloadSource" />
                </template>
                <template #tool-showForm="{ part }">
                    <transition mode="out-in">
                        <PkToolShowForm
                            :part
                            @select="
                                addToolOutput({
                                    tool: 'showForm',
                                    toolCallId: (part as any).toolCallId,
                                    output: $event,
                                })
                            " />
                    </transition>
                </template>
                <template #tool-showMultipleChoice="{ part }">
                    <transition mode="out-in">
                        <PkToolShowForm
                            :part
                            allow-custom-answer
                            @select="
                                addToolOutput({
                                    tool: 'showMultipleChoice',
                                    toolCallId: (part as any).toolCallId,
                                    output: $event,
                                })
                            " />
                    </transition>
                </template>
                <template #tool-requestConfirm="{ part }">
                    <PkToolRequestConfirm
                        :part
                        @confirm="
                            addToolOutput({
                                tool: 'requestConfirm',
                                toolCallId: (part as any).toolCallId,
                                output: $event ? 'confirmed' : 'cancelled',
                            })
                        " />
                </template>
                <template #tool-requestOAuthConnection="{ part }">
                    <PkToolRequestOAuthConnection
                        :part
                        :resolve-connection="
                            (serverName: string) =>
                                apiClient.getOAuthAuthorizeUrl(
                                    props.agentId,
                                    serverName,
                                )
                        "
                        @connected="
                            addToolOutput({
                                tool: 'requestOAuthConnection',
                                toolCallId: (part as any).toolCallId,
                                output: {
                                    connected: true,
                                    mcpServerId: $event,
                                },
                            })
                        " />
                </template>
                <template #tool-requestGeolocation="{ part }">
                    <PkToolRequestGeolocation
                        :part
                        :reverse-geocode="
                            (lat: number, lon: number) =>
                                apiClient.reverseGeocode(lat, lon)
                        "
                        @result="
                            addToolOutput({
                                tool: 'requestGeolocation',
                                toolCallId: (part as any).toolCallId,
                                output: $event,
                            })
                        " />
                </template>
                <template #tool-showLocation="{ part }">
                    <PkToolShowLocation
                        :part
                        :is-dark
                        :main-color="agentInterface?.mainColor"
                        :forward-geocode="
                            (query: string, lang?: string) =>
                                apiClient.forwardGeocode(query, lang)
                        " />
                </template>
                <template #tool-showDiagram="{ part }">
                    <PkToolShowDiagram :part :is-dark />
                </template>
            </PkChatbotMessages>
        </Transition>
        <!-- #endregion -->

        <!-- #region input -->
        <div class="pk-chatbot-view-chat__input-stage">
            <!-- Animated accent glow radiating from the input pill -->
            <Transition name="pk-chatbot-view-chat-fade">
                <PkAuroraCanvas
                    v-if="showEmptyState"
                    class="pk-chatbot-view-chat__aurora"
                    source-selector=".pk-chatbot-input__form" />
            </Transition>
            <div
                v-if="isConversationBlocked"
                class="p-md border-t border-surface-3 text-center text-12 text-danger-darken-2 bg-surface-danger">
                {{ $t('message.chatErrorConversationBlocked') }}
            </div>
            <PkChatbotInput
                v-else
                v-model="input"
                v-model:pending-attachments="pendingAttachments"
                :placeholder="inputMessagePlaceholder"
                :dismissable-notice="
                    dismissableNotice && chat.messages.length <= 1
                        ? dismissableNotice
                        : undefined
                "
                :status="chat.status"
                :max-message-length="agentInterface?.maxMessageLength"
                :file-upload="agentFileUpload"
                :attachment-limit-reached="isAttachmentLimitReached"
                :attachment-notice="attachmentNotice"
                @stop-generation="stopGeneration"
                @submit="storeHandleSubmit"
                @file-select="handleFileSelect"
                @dismiss-attachment-notice="clearFileLimitError">
                <!-- Fullscreen has no header: the agent identity lives in the
                 input pill -->
                <template v-if="modifier === 'fullscreen'" #prepend>
                    <span class="pk-chatbot-view-chat__agent-chip">
                        <PkAvatar
                            modifiers="surface"
                            class="pk-chatbot-view-chat__agent-chip-avatar"
                            :img-src="agentInterface?.logo"
                            :name="store.name" />
                        <strong
                            v-if="store.name"
                            class="pk-chatbot-view-chat__agent-chip-name">
                            {{ store.name }}
                        </strong>
                    </span>
                </template>
            </PkChatbotInput>
        </div>
        <!-- #endregion -->
        <Transition>
            <div
                v-if="isOverDropZone && agentFileUpload?.enabled"
                class="pk-chatbot-view-chat__drop-overlay">
                <VvIcon
                    name="ri:upload-cloud-2-line"
                    class="pk-chatbot-view-chat__drop-overlay-icon" />
                <span>{{ $t('action.dropFile') }}</span>
            </div>
        </Transition>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-view-chat {
        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;

        // Empty state (fullscreen): center the greeting + input block
        // vertically in the available space
        &--empty {
            justify-content: center;
            gap: var(--spacing-md);

            // On phones the input docks to the bottom edge instead
            // (Gemini-like), with the greeting centered in the space above
            @include media-breakpoint-down('xs', $breakpoints) {
                justify-content: flex-start;

                .pk-chatbot-empty-state {
                    flex: 1;
                    justify-content: center;
                }
            }
        }

        // Anchors the glow to the input pill
        &__input-stage {
            position: relative;
        }

        // Radiates outwards from the pill, biased upwards (the pill itself
        // paints above, being later in the DOM)
        &__input-stage &__aurora {
            inset: calc(-1 * var(--spacing-224)) calc(-1 * var(--spacing-96))
                calc(-1 * var(--spacing-160));

            // With the input docked at the bottom the glow becomes a wide
            // dome filling the lower part of the screen (Gemini-like)
            @include media-breakpoint-down('xs', $breakpoints) {
                --aurora-spread: 1.6;

                // Tall enough that the glow tail never meets the canvas
                // edge fade (it would draw a visible horizontal band)
                inset: calc(-1 * var(--spacing-384) - var(--spacing-128))
                    calc(-1 * var(--spacing-96)) calc(-1 * var(--spacing-160));
            }
        }

        // Agent identity inside the input pill (fullscreen has no header)
        &__agent-chip {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-6);
            flex-shrink: 0;
            padding: var(--spacing-4) var(--spacing-10) var(--spacing-4)
                var(--spacing-4);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-full);
            background-color: var(--color-surface);

            // Don't crowd the textarea on small screens
            @include media-breakpoint-down('md', $breakpoints) {
                display: none;
            }
        }

        &__agent-chip-avatar {
            width: var(--spacing-24);
            height: var(--spacing-24);
            font-size: var(--text-10);
            border-radius: var(--rounded-full);
        }

        &__agent-chip-name {
            font-size: var(--text-12);
            font-weight: var(--font-semibold);
            color: var(--color-word-2);
            white-space: nowrap;
        }

        // Crossfade empty state ↔ messages: exit faster than enter so the
        // switch never feels like a snap
        &-fade-enter-active {
            transition: opacity 250ms var(--ease-out);
        }

        &-fade-leave-active {
            transition: opacity 150ms var(--ease-out);
        }

        &-fade-enter-from,
        &-fade-leave-to {
            opacity: 0;
        }

        @media (prefers-reduced-motion: reduce) {
            &-fade-enter-active,
            &-fade-leave-active {
                transition: none;
            }
        }

        &__drop-overlay {
            position: absolute;
            inset: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm)
                var(--spacing-sm);
            z-index: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: var(--spacing-xs);
            background-color: color-mix(
                in srgb,
                var(--color-surface) 85%,
                transparent
            );
            border-radius: var(--rounded-xl);
            border: var(--spacing-2) dashed var(--color-surface-5);
            pointer-events: none;
            color: var(--color-word-3);

            &-icon {
                font-size: var(--spacing-32);
            }
        }
    }
</style>
